// ============================================================================
// describeError — turn anything thrown into something a user can act on.
//
// AFB-0054. supabase-js v2 returns PostgrestError as a PLAIN OBJECT, not an
// Error instance. The idiom
//
//     err instanceof Error ? err.message : 'Save failed. Please try again.'
//
// therefore throws away EVERY database error and shows the fallback string.
// That is why Jose's "Save failed. Please try again." was undiagnosable for 19
// days: the real error (whatever it was) never reached the screen, the console,
// or a screenshot. The idiom appears 205 times across src/.
//
// This helper unwraps PostgrestError, real Error instances and unknown
// throwables, and — for the constraint families that actually exist on the
// tables we write — translates the database's wording into a sentence that
// names the field and says what to do about it.
// ============================================================================

import { columnLabel, isSystemAssignedColumn } from './column-label';

export interface DescribedError {
  /** User-facing sentence. Always non-empty. */
  message: string;
  /** Postgres SQLSTATE or PostgREST code, when present. */
  code?: string;
  /** Raw database detail/hint, for the "technical detail" affordance. */
  detail?: string;
  /** Constraint name parsed out of the error, when identifiable. */
  constraint?: string;
  /**
   * The database's HINT, verbatim and on its own.
   *
   * AFB-0078. Our RPCs put a STABLE machine token here (TCS_TERM_NO_FACILITIES,
   * TCS_TERM_BAD_END_REASON, …) because the SQLSTATE cannot carry it: PostgREST
   * maps SQLSTATE to HTTP status by a fixed table and turns an unrecognised
   * class into a 500, so a bespoke code per validation would make every user
   * typo a server error. The SQLSTATE therefore carries the status (PTxxx) and
   * the hint carries the identity. Callers switch on this to attach the message
   * to the right field; `message` stays the human sentence either way.
   */
  hint?: string;
}

interface PostgrestLike {
  message?: unknown;
  code?: unknown;
  details?: unknown;
  hint?: unknown;
}

// ---------------------------------------------------------------------------
// Constraint → plain language.
//
// Keyed on the constraint name Postgres reports. Only constraints that really
// exist are listed; verified live against facility_tasks on 2026-07-29.
// A miss falls through to a generic-but-still-specific message that names the
// constraint, which beats "Save failed" by a wide margin.
// ---------------------------------------------------------------------------
const CONSTRAINT_MESSAGES: Record<string, string> = {
  // A subcontractor's cleaner (Ben, 28 Oct: option 2 on the one-cleaner-list item).
  workers_crew_has_employer:
    "Couldn't save — a Subcontractor's crew member works for a subcontractor. Pick the company in Works for, or choose another worker type for someone TCS pays.",
  // CHECK constraints — the allowed value lists.
  facility_tasks_priority_check:
    "Couldn't save — Priority must be Urgent, High, Medium or Low.",
  facility_tasks_status_check:
    "Couldn't save — Status must be Open, In Progress, Done or Deferred.",
  facility_tasks_source_check:
    "Couldn't save — the task's Source isn't one the system recognises. Reload the page and try again; if it persists this is a bug, not something you did wrong.",
  facility_tasks_category_check:
    "Couldn't save — Category must be Operations, Finance, HR, Sub Management, Sales or Other.",

  // Foreign keys — these are the ones a person can actually hit by choosing
  // the wrong thing in a picker.
  facility_tasks_assigned_to_fkey:
    "Couldn't save — the person selected in Assign to isn't a current employee record. Pick someone from the list rather than typing a name.",
  facility_tasks_created_by_fkey:
    "Couldn't save — your own employee record couldn't be matched, so the task has no valid author. Reload the page; if it happens again your profile may not be linked to an employee record yet.",
  facility_tasks_completed_by_fkey:
    "Couldn't save — the person recorded as completing this task isn't a current employee record.",
  facility_tasks_facility_id_fkey:
    "Couldn't save — that facility no longer exists or you don't have access to it. Pick the facility again from the list.",

  // account_terminations — AFB-0078. These two fire when something reaches the
  // table without going through rpc_create_account_termination. That should be
  // impossible from the app now, so seeing one of these is itself the bug
  // report: some path is still writing the table directly.
  account_terminations_scope_requires_facilities:
    "Couldn't save — a facility-scoped termination has to name at least one facility. Pick the facilities that are closing, or switch the scope to the whole account.",
  account_terminations_end_reason_code_fkey:
    "Couldn't save — the end reason isn't one of the current options. Re-pick it from the End reason list.",

  // app_feedback — CW-R. In practice the BEFORE trigger
  // trg_app_feedback_completion_guard raises P0001 first and this CHECK is
  // never reached from the app. It is mapped anyway: "unreachable in
  // practice" is precisely the assumption that leaves someone staring at
  // "a value on this form isn't one of the allowed options" the day a new
  // write path skips the trigger.
  app_feedback_completed_requires_evidence:
    "Couldn't complete this item — a feedback item can only be closed with a written resolution. Say what resolved it and save again.",

  // Separations. In practice hr_start_separation raises its own P0001 with a
  // TCS_ hint before most of these are reached; they are mapped anyway, because
  // "unreachable from the app" is exactly the assumption that leaves somebody
  // reading a raw constraint name the day a new write path skips the RPC.
  employees_employment_status_check:
    "Couldn't save — an employee is either Active or Former. 'Archived' was retired: it meant the same thing as Former and no rule told them apart.",
  employees_separation_type_check:
    "Couldn't save — that isn't one of the seven separation reasons. 'Voluntary' and 'Separated' were replaced, not renamed.",
  employee_separations_separation_type_check:
    "Couldn't save — that isn't one of the seven separation reasons. Pick the reason it happened, not the fact that it did.",
  employee_separations_severance_dates_chk:
    "Couldn't save — the severance dates are out of order. Pay has to continue at least to the last working day, and benefits at least as long as the pay does. Getting these backwards ends somebody's benefits early.",
  employee_separations_severance_payment_method_check:
    "Couldn't save — severance is paid either as continued payroll or as a lump sum.",
  employee_separations_source_check:
    "Couldn't save — a separation is recorded through the app, written by a client conversion, or backfilled from history. Nothing else writes this table.",
  employee_separations_status_check:
    "Couldn't save — a separation is In Progress, Completed or Cancelled.",
  separation_checklist_items_owner_chk:
    "Couldn't save — a checklist item is owned by the COO, HR, Finance, the National AM or the Supervisor. Ownership is a position, never a person.",
  separation_checklist_items_governing_chk:
    "Couldn't save — an item is driven by the last working day, the pay-through date, the benefits-through date, HR sign-off, or it happens immediately.",
  separation_checklist_items_unique_key:
    'That checklist item already exists on this separation. Rebuilding a checklist adds what is new and never duplicates or resets what is already there.',
  separation_checklist_items_assigned_to_eid_fkey:
    "Couldn't assign that — the person picked isn't a current employee record. Pick somebody from the list rather than typing a name.",
  employee_separations_supervisor_eid_fkey:
    "Couldn't save — the supervisor picked isn't a current employee record.",

  // The market vocabulary got one home on 2026-09-10. Both sides now carry a
  // real foreign key rather than a copied CHECK, so a market that does not
  // exist is refused by name and this is what the person reads.
  facilities_market_region_fkey:
    "Couldn't save — that isn't one of the markets we operate in. Markets are a list somebody maintains, so if this one is real it needs adding to the list first.",
  new_account_sop_market_region_fkey:
    "Couldn't save — that isn't one of the markets we operate in. Markets are a list somebody maintains, so if this one is real it needs adding to the list first.",
};

/**
 * The market-does-not-cover-the-state sentence, in ONE place.
 *
 * The DB guard (TCS_MARKET_DOES_NOT_COVER_STATE) refuses the pairing on the way
 * in and the new-account form refuses it at entry. Both read this, so the two
 * cannot drift into saying different things about the same rule — the mistake
 * that cost a whole run when a blocker message lived in two components under a
 * header comment promising it did not (TCSD-0173).
 *
 * ⛔ It names BOTH sides. "Invalid market" tells somebody nothing; the state
 * and the market together tell them which one to change.
 */
export function MARKET_STATE_MISMATCH_HINT(
  state: string,
  market: string,
): string {
  return `This building is in ${state}, and ${market} doesn't cover ${state}. Fix the address or pick the market that covers it.`;
}

// Field-level fallbacks moved to ./column-label. The map that used to live here
// held about a dozen facility_tasks columns and every other column in the
// database fell through to its RAW NAME, dressed up in a confident sentence —
// see that file's header for what that cost on 2026-08-24.

function extractConstraint(text: string): string | undefined {
  // Postgres phrasing: violates check constraint "foo_check"
  //                    violates foreign key constraint "foo_fkey"
  //                    duplicate key value violates unique constraint "foo_key"
  const m = /constraint "([^"]+)"/i.exec(text);
  return m?.[1];
}

function extractColumn(text: string): string | undefined {
  // null value in column "assigned_to" violates not-null constraint
  const quoted = /column "([^"]+)"/i.exec(text);
  if (quoted?.[1]) return quoted[1];
  // Foreign-key detail phrasing: Key (assigned_to)=(EID-9999) is not present…
  const key = /Key \(([^)]+)\)=/i.exec(text);
  if (key?.[1]) return key[1].split(',')[0].trim();
  return undefined;
}

/**
 * Last resort: recover the column from the constraint name.
 *
 * Postgres puts the column in DETAIL, not in the message, for a foreign-key
 * violation — and DETAIL does not always survive the trip. But our FK names
 * follow `<table>_<column>_fkey`: 252 of 258 do, measured live 2026-08-24. The
 * table name is in the message, so stripping it off both ends leaves the
 * column.
 *
 * Returns undefined for the six that don't follow the convention, and the
 * caller falls back to the generic sentence — the same one it used to give
 * every time.
 */
function columnFromConstraint(text: string, constraint: string): string | undefined {
  const rel = /on table "([^"]+)"|relation "([^"]+)"/i.exec(text);
  const table = rel?.[1] ?? rel?.[2];
  if (!table) return undefined;
  const prefix = `${table}_`;
  const suffix = '_fkey';
  if (!constraint.startsWith(prefix) || !constraint.endsWith(suffix)) return undefined;
  const col = constraint.slice(prefix.length, -suffix.length);
  return col === '' ? undefined : col;
}

/**
 * Describe any thrown value. Never throws, always returns a usable message.
 *
 * @param err       whatever landed in the catch block
 * @param fallback  message to use when nothing better can be determined
 */
export function describeError(err: unknown, fallback = 'Something went wrong.'): DescribedError {
  const described = describeErrorInner(err, fallback);
  const hint = rawHint(err);
  return hint ? { ...described, hint } : described;
}

/**
 * The database's HINT on its own, un-merged with `details`.
 *
 * `describeErrorInner` folds hint into `detail` for the technical-detail
 * affordance, which is right for display and useless for dispatch — a caller
 * cannot switch on "details · hint". AFB-0078 needs the token intact.
 */
function rawHint(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const h = (err as PostgrestLike).hint;
    if (typeof h === 'string' && h.trim() !== '') return h.trim();
  }
  return undefined;
}

function describeErrorInner(err: unknown, fallback: string): DescribedError {
  // --- unwrap ---------------------------------------------------------------
  let raw = '';
  let code: string | undefined;
  let detail: string | undefined;

  if (err instanceof Error) {
    raw = err.message ?? '';
  } else if (err && typeof err === 'object') {
    const e = err as PostgrestLike;
    if (typeof e.message === 'string') raw = e.message;
    if (typeof e.code === 'string' && e.code) code = e.code;
    const bits = [e.details, e.hint].filter(
      (x): x is string => typeof x === 'string' && x.trim() !== '',
    );
    if (bits.length) detail = bits.join(' · ');
  } else if (typeof err === 'string') {
    raw = err;
  }

  const haystack = [raw, detail].filter(Boolean).join(' ');
  const constraint = haystack ? extractConstraint(haystack) : undefined;

  // --- delete blocked by a dependent row ------------------------------------
  //
  // AFB-0084 / item #542. Postgres words a delete blocked by a child row
  // differently from an insert/update that points at something missing: the
  // message itself, not just DETAIL, names the table that still points here
  // ("update or delete on table … violates foreign key constraint … on table
  // X"). That means this still works even when DETAIL is lost on the way to
  // the screen. Every blocked delete gets this sentence, ahead of the
  // save-side messages below, which were never written with a delete in mind.
  const deleteBlockedTable =
    /update or delete on table "[^"]+" violates foreign key constraint "[^"]+" on table "([^"]+)"/i.exec(
      haystack,
    )?.[1];
  if (deleteBlockedTable) {
    // ⛔ The ONE case with its own words: a project an invoice line bills. Ben,
    // 2026-10-14 (TCSD-0490): a project on an invoice is never deleted
    // outright, so "remove it from there first" is the wrong advice — the fix
    // is to point the line at the right project. Wording picked by Ben on the
    // #542 demo page, 2026-10-19 (Decision 4). Every other table keeps the
    // shared sentence below.
    if (/foreign key constraint "invoice_line_items_source_project_id_fkey"/i.test(haystack)) {
      return {
        message:
          "Couldn't delete — this project is on an invoice, so nothing was deleted. If the invoice should name a different project, Finance can change it on the invoice.",
        code,
        detail,
        constraint,
      };
    }
    const label = columnLabel(deleteBlockedTable) ?? deleteBlockedTable;
    const what = label.charAt(0).toLowerCase() + label.slice(1);
    return {
      message: `Couldn't delete — this is still used by ${what}, so nothing was deleted. Remove it from there first; if you can't see where, send it through the feedback button.`,
      code,
      detail,
      constraint,
    };
  }

  // --- session expiry: the one case with a specific remedy ------------------
  if (code === 'PGRST301' || /jwt expired/i.test(raw)) {
    return {
      message:
        'Your session expired and could not be refreshed automatically. Reload the page or sign in again, then retry.',
      code,
      detail,
    };
  }

  // --- known constraint ----------------------------------------------------
  if (constraint && CONSTRAINT_MESSAGES[constraint]) {
    return { message: CONSTRAINT_MESSAGES[constraint], code, detail, constraint };
  }

  // --- unknown constraint, but we know the family --------------------------
  if (constraint) {
    if (/_fkey$/.test(constraint)) {
      const col = extractColumn(haystack) ?? columnFromConstraint(haystack, constraint);
      const label = columnLabel(col);
      return {
        message: label
          ? `Couldn't save — the value chosen for ${label} doesn't match an existing record. Pick it from the list rather than typing it.`
          : `Couldn't save — one of the selected records (${constraint}) no longer exists. Reload the page and pick it again.`,
        code,
        detail,
        constraint,
      };
    }
    if (/_check$/.test(constraint)) {
      return {
        message: `Couldn't save — a value on this form isn't one of the allowed options (${constraint}). Re-pick the dropdown fields and try again.`,
        code,
        detail,
        constraint,
      };
    }
    if (/(_key|_unique)$/.test(constraint)) {
      return {
        message: `Couldn't save — a record like this already exists (${constraint}). Check whether someone has already created it.`,
        code,
        detail,
        constraint,
      };
    }
  }

  // --- not-null on a named column ------------------------------------------
  //
  // TWO sentences, not one. The old code had a single "you left it empty"
  // sentence and used it for every column, including columns no form has ever
  // shown. That is how "vehicle_id is required and was left empty" reached a
  // user about a primary key a database trigger assigns (2026-08-24, Fleet).
  //
  // Blaming somebody for a field they cannot see is worse than saying nothing,
  // because they will go looking for it — and they will not find it.
  if (/not-null constraint/i.test(haystack)) {
    const col = extractColumn(haystack);
    const label = columnLabel(col);

    if (col && isSystemAssignedColumn(col)) {
      return {
        message:
          `Couldn't save — the record is missing something the system fills in ` +
          `automatically (${col}), so this is a bug rather than anything you did ` +
          `wrong. Nothing was saved. Worth reporting with this message.`,
        code,
        detail,
      };
    }

    return {
      message: label
        ? `Couldn't save — ${label} is required and was left empty.`
        : "Couldn't save — a required field was left empty.",
      code,
      detail,
    };
  }

  // --- RLS / permission ----------------------------------------------------
  if (
    code === '42501' ||
    /row-level security|permission denied|violates row-level/i.test(haystack)
  ) {
    return {
      message:
        "Couldn't save — your role doesn't have permission to write this record. If you believe it should, this is a permissions bug worth reporting.",
      code,
      detail,
    };
  }

  // --- offline / transport -------------------------------------------------
  if (/failed to fetch|networkerror|load failed/i.test(raw)) {
    return {
      message:
        "Couldn't reach the server. Check your connection and try again — nothing was saved.",
      code,
      detail,
    };
  }

  // --- anything else: the raw database message beats the fallback -----------
  return { message: raw.trim() || fallback, code, detail, constraint };
}

/**
 * Convenience wrapper for the common `setError(...)` case.
 *
 * ALWAYS logs the raw throwable plus the parsed constraint, so a user's
 * screenshot of the console names the constraint even when the on-screen
 * sentence is deliberately non-technical. That is the whole point of AFB-0054:
 * the next failure must be diagnosable from what the reporter can see.
 */
export function errorMessage(err: unknown, fallback = 'Something went wrong.', context?: string): string {
  const d = describeError(err, fallback);
  console.error(
    `[${context ?? 'error'}] ${d.message}`,
    { code: d.code, constraint: d.constraint, detail: d.detail, raw: err },
  );
  return d.message;
}
